import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  exportReferralCandidatesThunk,
  fetchReferredCandidatesThunk,
} from "../../../store/superadmin/referral/thunks";
import {
  ReferralSortField,
  SortOrder,
} from "../../../store/superadmin/referral/types";
import { getCandidateData } from "../../../store/superadmin/thunks";
import PaginationControls from "../../Pagination/PaginationControls";
import CandidateReviewModal from "../Candidates/CandidateReviewModal/Index";
import ReferralCard from "./ReferralCard";
import ReferralTableHeader from "./ReferralTableHeader";
import SearchAndFilter from "./SearchAndFilter";

const ReferralTracker: FC = () => {
  const dispatch = useDispatch();
  const { referralList, pagination } = useSelector((state) => state.referrals);
  const referralArray = Object.values(referralList);
  // Local states for sorting
  const [sortBy, setSortBy] = useState<ReferralSortField>("first_name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  // Search and debouncing states
  const [searchInput, setSearchInput] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  // Candidate review modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentRefereeId, setCurrentRefereeId] = useState<number>(0);
  const [reviewReferee, setReviewReferee] = useState<any>(null); // Type it properly if type is known

  const [selectCandidatesCompletedCheckbox, setSelectCandidatesCompletedCheckbox] = useState<boolean>(false)

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  // Handle sorting logic
  const handleSortChange = (field: ReferralSortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  // Fetch referral list (debounced, sorted, paginated)
  const fetchReferralList = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());

    dispatch(
      fetchReferredCandidatesThunk({
        keyword,
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        jobs_completed: selectCandidatesCompletedCheckbox
      })
    ).finally(() => {
      dispatch(hideTransparentPreloaderAction());
    });
  }, [dispatch, keyword, page, perPage, sortBy, sortOrder, selectCandidatesCompletedCheckbox]);

  // Call fetch function on filter/pagination/sort change
  useEffect(() => {
    fetchReferralList();
  }, [fetchReferralList, keyword, page, perPage, sortBy, sortOrder, selectCandidatesCompletedCheckbox]);

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);
      setPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);

  const handleExportCSV = async () => {
    try {
      const result = await dispatch(
        exportReferralCandidatesThunk({
          keyword,
          sort_by: sortBy,
          sort_order: sortOrder,
          jobs_completed: selectCandidatesCompletedCheckbox
        })
      );

      // Check if result is an error or contains valid data
      if (result?.payload && typeof result.payload === "string") {
        const csvContent = result.payload; // The CSV string content from the response

        // Convert CSV string to a Blob
        const blob = new Blob([csvContent], { type: "text/csv;charset=UTF-8" });

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary download link
        const a = document.createElement("a");
        a.href = url;
        a.download = "referral_candidates.csv";
        document.body.appendChild(a);
        a.click();
        a.remove(); // Remove the link after triggering the download

        // Release the object URL to free up memory
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Export failed: Invalid CSV data", result);
      }
    } catch (error) {
      console.error("Unexpected error during export:", error);
    }
  };

  const handleRefereeClick = async (refereeUUID: string, refereeId: number) => {
    try {
      dispatch(showTransparentPreloaderAction());
      const candidateResponse = await dispatch(
        getCandidateData({ candidate_id: refereeUUID })
      );
      dispatch(hideTransparentPreloaderAction());
      if (isFulfilled(candidateResponse)) {
        setReviewReferee(candidateResponse?.payload?.data?.[0]);
        setCurrentRefereeId(refereeId);
        setShowReviewModal(!false);
      } else {
        setShowReviewModal(false);
        toast.error("Something went wrong while fetching candidate data.");
      }
    } catch (err) {
      console.error("Error fetching candidate data:", err);
      toast.error("Something went wrong while fetching candidate data.");
      hideTransparentPreloaderAction();
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-10">
        <SearchAndFilter
          keyword={searchInput}
          onSearchChange={handleSearch}
          onExportCSV={handleExportCSV}
          selectCandidatesCompletedCheckbox={selectCandidatesCompletedCheckbox}
          setSelectCandidatesCompletedCheckbox={setSelectCandidatesCompletedCheckbox}
        />
        <div className="d-flex flex-column">
          <ReferralTableHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
          {referralArray.length > 0 ? (
            referralArray.map((referrer, index) => (
              <div key={index}>
                <ReferralCard
                  referrerCount={referralList.length}
                  key={referrer.id}
                  referrer={referrer}
                  onRefereeClick={handleRefereeClick}
                />
              </div>
            ))
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">No referral candidates found{" "}</div>
            </div>
          )}

          {/* Render pagination controls if there are more than 10 users */}
          {pagination && pagination?.total > 10 && (
            <PaginationControls
              currentPage={page} // Current page number
              totalPages={pagination?.last_page} // Total number of pages
              paginationLinks={pagination?.links} // List of pagination links
              onPageChange={handlePageChange} // Function to handle page change
              setPerPageList={setPerPage} // Function to set the number of items per page
              setCurrentPage={setPage} // Function to set the current page
            />
          )}
        </div>
      </div>
      {/* Candidate Review Modal */}
      {reviewReferee && showReviewModal && (
        <CandidateReviewModal
          showModal={showReviewModal}
          setShowModal={setShowReviewModal}
          currentCandidateId={currentRefereeId}
          reviewCandidateData={reviewReferee}
          currentCandidateUUID={reviewReferee?.uuid}
          currentCandidatePrimaryId={reviewReferee?.id}
        />
      )}
    </>
  );
};

export default ReferralTracker;
